import React from "react";
import type { SimpleTableConfigBase, BaseEntity } from "../types";
import type { FilterState } from "@/components/shared/types";
import { CompanySelectFilter, ClientSelectFilter, UserSelectFilter } from "./filter-rendering-selects";
import { DatetimePresetFilter } from "./filter-rendering-datetime";
import { TagsOrJsonFilter, OptionsMultiSelectFilter, BooleanFilter } from "./filter-rendering-options";

export type { QuickFilterField } from "./filter-rendering-helpers";
export { getQuickFilterFields } from "./filter-rendering-helpers";

interface DatetimePreset {
  value: string;
  label: string;
}

/**
 * Renders filter dropdowns for all filterable fields in config.
 * Reusable between SimpleTable and GroupedListView.
 *
 * Supports:
 * - Fields with options (select, multi-select, etc.)
 * - Datetime fields with presets
 * - Company / Client / User select fields
 * - Boolean fields
 */
export function renderFilterDropdowns<T extends BaseEntity = BaseEntity>(
  config: SimpleTableConfigBase<T>,
  filters: FilterState,
  onFilterChange: (filters: FilterState) => void,
  getDatetimePresets?: () => DatetimePreset[],
  handleCustomRangeClick?: (fieldId: string, label: string) => void,
  useGridLayout?: boolean
): React.ReactNode {
  if (!config.features?.filters) return null;

  const filterElements = Object.entries(config.fields).map(([fieldId, fieldConfig]) => {
    if (!fieldConfig.filterable || fieldConfig.formOnly) return null;

    const props = { fieldId, fieldConfig, filters, onFilterChange };

    if (fieldConfig.filterType === "company-select") {
      return <CompanySelectFilter key={fieldId} {...props} />;
    }

    if (fieldConfig.filterType === "client-select") {
      return <ClientSelectFilter key={fieldId} {...props} />;
    }

    if (fieldConfig.filterType === "user-select") {
      return <UserSelectFilter key={fieldId} {...props} />;
    }

    if (
      (fieldConfig.type === "datetime" || fieldConfig.type === "date") &&
      fieldConfig.datetimePresets?.enabled
    ) {
      if (!getDatetimePresets) return null;
      return (
        <DatetimePresetFilter
          key={fieldId}
          {...props}
          getDatetimePresets={getDatetimePresets}
          handleCustomRangeClick={handleCustomRangeClick}
        />
      );
    }

    if (
      (fieldConfig.type === "tags" || fieldConfig.type === "json") &&
      fieldConfig.options &&
      fieldConfig.options.length > 0
    ) {
      return <TagsOrJsonFilter key={fieldId} {...props} />;
    }

    if (fieldConfig.options && fieldConfig.options.length > 0) {
      return <OptionsMultiSelectFilter key={fieldId} {...props} />;
    }

    if (fieldConfig.type === "boolean") {
      return <BooleanFilter key={fieldId} {...props} />;
    }

    return null;
  });

  if (useGridLayout) {
    return filterElements;
  }

  return <div className="flex flex-wrap gap-2 items-start">{filterElements}</div>;
}
